import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo, ReactNode, useCallback, useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";

export interface ContextMenuItem {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  variant?: "default" | "danger";
}

interface ContextMenuProps {
  /** The element that triggers the menu (e.g., an icon or button) */
  trigger: ReactNode;
  /** List of actions to display in the menu */
  items: ContextMenuItem[];
  /** Optional class name for the wrapper container */
  className?: string;
  /** Optional class name for the menu panel itself */
  menuClassName?: string;
}

/**
 * A context menu component that appears relative to a triggerer button.
 * Positioned by default at the 'top-right' (above the button, aligned to the right edge).
 */
export const ContextMenu = memo(function ContextMenu({
  trigger,
  items,
  className,
  menuClassName,
}: ContextMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);

  const handleToggle = useCallback(() => {
    if (!isOpen && containerRef.current) {
      setTriggerRect(containerRef.current.getBoundingClientRect());
    }
    setIsOpen((prev) => !prev);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const menuStyles = useMemo(() => {
    if (!triggerRect) return {};

    // Anchors the menu above the trigger button, aligned to its right edge.
    return {
      position: "fixed" as const,
      top: triggerRect.top,
      right: window.innerWidth - triggerRect.right,
      zIndex: 50,
    };
  }, [triggerRect]);

  return (
    <div className={cn("relative inline-block", className)} ref={containerRef}>
      <div onClick={handleToggle} className="cursor-pointer">
        {trigger}
      </div>

      {isOpen &&
        createPortal(
          <>
            {/* 0.4 black backdrop */}
            <div
              className="fixed inset-0 z-40 bg-black/40"
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
            />
            <div
              style={menuStyles}
              className={cn(
                "min-w-[180px] overflow-hidden rounded-2xl bg-white p-1 select-none",
                "shadow-[0_4px_20px_rgba(0,0,0,0.1)] ring-1 ring-black/5",
                menuClassName,
              )}
            >
              <div className="flex flex-col py-0">
                {items.map((item, index) => (
                  <button
                    key={`${item.label}-${index}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      item.onClick();
                      handleClose();
                    }}
                    className={cn(
                      "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors rounded-xs",
                      "hover:bg-gray-50 active:bg-gray-50",
                      item.variant === "danger" ? "text-red-500" : "text-gray-700",
                    )}
                  >
                    {item.icon && (
                      <span className="flex h-5 w-5 items-center justify-center">{item.icon}</span>
                    )}
                    <span className={cn(textVariants.body, "font-medium")}>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
});